import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api.js';
import { URLS, ImgURL } from '../utils/urls.js';
import { Trash2, ChevronLeft, ChevronRight, ZoomIn, User, X } from 'lucide-react';
import { Link } from "react-router-dom";
import { useTopLoader } from "../context/TopLoaderContext";

const formatFileSize = (bytes) => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

const MediaGalleryPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMedia, setSelectedMedia] = useState(new Set());
  const [totalSize, setTotalSize] = useState(0);
  const [preview, setPreview] = useState(null);
      const { start, stop } = useTopLoader();

  const limit = 12;

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      start();
      const res = await api.get(URLS.GET_MEDIA, { params: { page, limit } });
      setMedia(res.data.media || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalSize(res.data.totalSize || 0);
    } catch (err) {
      console.error('Failed to fetch media:', err);
      setError('Failed to fetch media. Please try again.');
    } finally {
      setLoading(false);
      stop();
    }
  }, [page, limit]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Select single media
  const handleSelect = (id) => {
    const newSelected = new Set(selectedMedia);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedMedia(newSelected);
  };

  // Select all
  const handleSelectAll = () => {
    if (selectedMedia.size === media.length) {
      setSelectedMedia(new Set());
    } else {
      setSelectedMedia(new Set(media.map((m) => m.id)));
    }
  };

  // Bulk delete
  const handleDelete = async () => {
    if (!window.confirm(`Delete ${selectedMedia.size} media file(s)?`)) return;
    try {
      for (const id of selectedMedia) {
        await api.delete(`${URLS.GET_MEDIA}/${id}`);
      }
      setSelectedMedia(new Set());
      fetchMedia();
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete media.');
    }
  };

  return (
    <div className="p-2  bg-gray-100 min-h-screen font-sans ">
      <div className="flex justify-between items-center mb-10">
        <h2 className="text-3xl font-bold text-gray-800">Media Gallery</h2>

              {/* Bulk actions */}
        <div className="mb-4 flex items-center gap-4">
          <div className="text-gray-700 font-medium mr-4 py-1">
            Total Storage: {formatFileSize(totalSize)}
          </div>
      {selectedMedia.size > 0 && (
        <>
          <span className="text-gray-600 font-medium">
            Selected: {selectedMedia.size}
          </span>
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-1 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            <Trash2 size={16} /> Delete
          </button>
          </>
      )}
        </div>
      </div>



      {/* Media Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
        {loading && <div>Loading...</div>}
        {error && <div className="text-red-500">{error}</div>}
        {!loading &&
          !error &&
          media.map((m) => (
            <div
              key={m.id}
              className="relative bg-white rounded-xl shadow overflow-hidden group cursor-pointer"
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                className="absolute top-2 left-2 z-10 h-4 w-4"
                checked={selectedMedia.has(m.id)}
                onChange={() => handleSelect(m.id)}
              />
              {/* Image */}
              <img
                src={ImgURL(m.thumb150 || m.url)}
                alt="media"
                className="w-full object-cover aspect-[3/4] rounded-md"
                onClick={() => setPreview(m)}
              />
              {/* File size overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-2 py-1 flex justify-between">
                <span>{formatFileSize(m.size)}</span>
                <ZoomIn size={14} className="opacity-80" />
              </div>
            </div>
          ))}
      </div>

      {/* Pagination */}
      <div className="mt-10 flex justify-between items-center">
        <button
          onClick={() => setPage((p) => p - 1)}
          disabled={page <= 1}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ChevronLeft size={16} /> Previous
        </button>
        <span className="text-gray-600">
          Page <span className="font-semibold">{page}</span> of{' '}
          <span className="font-semibold">{totalPages}</span>
        </span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= totalPages}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Next <ChevronRight size={16} />
        </button>
      </div>

      {/* Preview Modal */}
      {/* Image Preview Modal */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() => setPreview(null)} // close when clicking outside
        >
          <div
            className="relative"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            {/* User Overlay */}
            <Link
              to={`/admin/users/${preview.user_id}`}
              className="absolute top-2 left-2 z-20 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full hover:bg-black/80 transition"
            >
              {preview.profile_photo_url ? (
                <img
                  src={ImgURL(preview.profile_photo_url)}
                  alt="profile"
                  className="h-7 w-7 rounded-full object-cover border"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center">
                  <User size={14} className="text-gray-600" />
                </div>
              )}
              <span className="text-white text-sm font-medium">
                {`${preview.first_name || ''} ${preview.last_name || ''}`.trim() ||
                  preview.username}
              </span>
            </Link>

            {/* Image */}
            <img
              src={ImgURL(preview.thumb720 || preview.url)}
              alt="Preview"
              className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-lg"
            />

            {/* Close Button */}
            <button
              onClick={() => setPreview(null)}
              className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100"
            >
              <X size={22} className="text-gray-700" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default MediaGalleryPage;
